import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { NgxSpinnerService } from 'ngx-spinner';
import { PhoneNumberUtil } from 'google-libphonenumber';
import {
  firstValueFrom,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import {
  invitationApi,
  regionsApi,
  signupApi,
  signupCreatorApi,
} from 'src/app/shared/constants/api.constants';
import { ICity } from 'src/app/shared/models/ICity.model';
import { IRegion } from 'src/app/shared/models/IRegion.model';
import { HelpersService, HttpBaseService } from '@evento-ng/libs';

@Component({
  selector: 'app-register-type',
  templateUrl: './signup.component.html',
})
export class SignupComponent implements OnInit, OnDestroy {
  public signupForm: UntypedFormGroup;
  signupType: string;
  isAgree: boolean;
  subs: Subscription[] = [];
  invitation: string;
  regions$: Observable<IRegion[]>;
  cities$: Observable<ICity[]>;
  constructor(
    public formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dataService: HttpBaseService,
    @Inject(DOCUMENT) private document: Document,
    private helpers: HelpersService,
    private recaptchaV3Service: ReCaptchaV3Service,
    public spinner: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.route.queryParams.subscribe(
        (query) => (
          (this.signupType = query.type), (this.invitation = query.invitation)
        )
      )
    );
    if (this.invitation) {
      this.getInvitationInfo();
    }
    this.initSignup('', '', 'SA');
    if (this.signupType === 'COMPANY') {
      this.addControl('commercialName', null, false, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(30),
        this.noWhitespaceValidator(),
      ]);
      this.addControl('address', null, false, [
        Validators.required,
        this.noWhitespaceValidator(),
      ]);
      this.addControl('region', null, false, [
        Validators.required,
        this.noWhitespaceValidator(),
      ]);
      this.addControl('city', null, true, [
        Validators.required,
        this.noWhitespaceValidator(),
      ]);
    }

    this.getRegions();

    this.cities$ = this.signupForm?.controls.region?.valueChanges.pipe(
      switchMap((id) => {
        if (id) {
          this.signupForm.controls.city.enable();
        } else {
          this.signupForm.controls.city.patchValue(null);
          this.signupForm.controls.city.disable();
        }
        return id ? this.getCities(id) : of(null);
      })
    );
  }

  getInvitationInfo() {
    this.dataService.get(`${invitationApi}/${this.invitation}`).subscribe(
      (res) => {
        this.initSignup(
          res.email,
          this.formatMobile(res.mobile),
          this.formatCountryCode(res.mobile)
        );
      },
      (err) => {
        this.router.navigate(['/auth/invalid-invitation']);
      }
    );
  }

  formatMobile(mobile: string) {
    const phoneNumberUtil = PhoneNumberUtil.getInstance();
    const parsedNumber = phoneNumberUtil.parse(mobile);
    return parsedNumber.values_[2];
  }

  formatCountryCode(mobile) {
    const phoneNumberUtil = PhoneNumberUtil.getInstance();
    // const parsedNumber = phoneNumberUtil.parse(mobile);
    const number = phoneNumberUtil.parseAndKeepRawInput(mobile);
    return phoneNumberUtil.getRegionCodeForNumber(number);
  }

  getRegions(): void {
    this.regions$ = this.dataService.get(regionsApi).pipe(
      map((regions) => {
        let newRegions = [];
        newRegions = regions.map((region) => {
          return {
            ...region,
            name: this.helpers.getElementTranslatedName(region),
          };
        });

        return newRegions;
      })
    );
  }
  getCities(id: string): Observable<ICity[]> {
    return this.dataService.get(`${regionsApi}/${id}/cities`).pipe(
      map((cities) => {
        let newCities = [];
        newCities = cities.map((city) => {
          return {
            ...city,
            name: this.helpers.getElementTranslatedName(city),
          };
        });

        return newCities;
      })
    );
  }

  private initSignup(email: string, mobile: string, countryCode: string): void {
    this.signupForm = this.formBuilder.group(
      {
        firstName: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(30),
            this.noWhitespaceValidator(),
            this.helpers.noSpecialCharsAndTrim,
          ],
        ],
        lastName: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(30),
            this.noWhitespaceValidator(),
            this.helpers.noSpecialCharsAndTrim,
          ],
        ],
        email: [
          { value: email, disabled: this.invitation },
          [Validators.required, Validators.email, this.noWhitespaceValidator()],
        ],
        //gender: ['', [Validators.required]],
        mobileRegionCode: [countryCode],
        mobile: [
          { value: mobile, disabled: this.invitation },
          [Validators.required],
        ],
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: [
          '',
          [Validators.required, Validators.minLength(6)],
        ],
      },
      {
        validators: [this.MustMatch('password', 'confirmPassword')],
      }
    );
  }

  MustMatch(controlName: string, matchingControlName: string): any {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  toggleAgree(event): void {
    if (event.target.checked) {
      this.isAgree = true;
      return;
    }
    this.isAgree = false;
  }

  private addControl(
    name: string,
    value = null,
    disabled = false,
    validators?: any[]
  ): void {
    this.signupForm.addControl(
      name,
      new UntypedFormControl({ value, disabled }, validators)
    );
  }
  signup(): void {
    this.spinner.show();
    firstValueFrom(
      this.recaptchaV3Service.execute('signUp').pipe(
        switchMap((token) => {
          const body = {
            ...this.signupForm.getRawValue(),
            recaptcha: token,
            mobile: this.signupForm.getRawValue().mobile.number,
            mobileRegionCode: this.signupForm.getRawValue().mobile.countryCode,
            firstName: this.signupForm.getRawValue().firstName.trim(),
            lastName: this.signupForm.getRawValue().lastName.trim(),
          };
          const apiUrl =
            this.signupType === 'COMPANY'
              ? `${signupCreatorApi}`
              : `${signupApi}`;
          return this.dataService.post(`${apiUrl}`, body);
        }),
        tap((res) => {
          this.spinner.hide();
          this.router.navigate(['/auth/otp'], {
            queryParams: {
              id: res.id,
              mobile: this.signupForm.getRawValue().mobile.number,
              mobileRegionCode:
                this.signupForm.getRawValue().mobile.countryCode,
            },
          });
        })
      )
    );
  }

  openLinkInNewWindow(path: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`${path}`])
    );

    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';
      const isWhitespace = value.trim().length === 0;
      return isWhitespace ? { required: true } : null;
    };
  }
}
